import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OtpPanel } from "@/components/common/OtpPanel";
import { AuthShell, AuthBrandHeader } from "@/components/common/AuthShell";

const LANGS = ["English", "Hindi", "Telugu", "Tamil", "Kannada"];

export default function CustomerSignup() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", language: "English", referral_code: "" });
  const [step, setStep] = useState("form");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const next = () => {
    if (!f.name.trim()) return toast.error("Enter your full name");
    if (f.phone.length < 10) return toast.error("Enter a valid 10-digit mobile number");
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return toast.error("Enter a valid email");
    setStep("otp");
  };
  const onVerified = (data) => { login(data.token, data.user); toast.success("Welcome to BiteGoFood!"); nav("/"); };

  return (
    <AuthShell maxWidth="460px">
      <div data-testid="customer-signup">
        <button onClick={() => (step === "otp" ? setStep("form") : nav("/login"))} className="w-9 h-9 -ml-1 mb-1 flex items-center justify-center text-ink hover:text-coral transition-colors tap" aria-label="Back"><ArrowLeft size={22} /></button>
        <AuthBrandHeader />

        <div className="bg-white border border-border rounded-3xl shadow-sm p-6 sm:p-7">
          {step === "otp" ? (
            <OtpPanel phone={f.phone} email={f.email} role="customer" title="Verify your account"
              extra={{ name: f.name, city: f.city, language: f.language, referral_code: f.referral_code }} onBack={() => setStep("form")} onVerified={onVerified} />
          ) : (
            <div className="space-y-3.5">
              <div>
                <h2 className="font-display font-extrabold text-2xl text-ink tracking-tight">Create your account</h2>
                <p className="text-sm text-ink-600 mt-1">Join BiteGoFood — Food Near You, Faster Than Ever.</p>
              </div>
              <Field label="Full name" required><input value={f.name} onChange={set("name")} placeholder="Your name" className="inp" data-testid="signup-name" /></Field>
              <Field label="Mobile number" required>
                <div className="flex mt-1"><span className="bg-surface border border-border border-r-0 rounded-l-xl px-3.5 flex items-center text-sm font-semibold text-ink-600">+91</span>
                  <input value={f.phone} onChange={(e) => setF((p) => ({ ...p, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} placeholder="10-digit number" inputMode="numeric" className="flex-1 min-w-0 bg-surface rounded-r-xl px-3.5 py-3 text-sm outline-none focus:ring-2 ring-coral" data-testid="signup-phone" /></div>
              </Field>
              <Field label="Email" required><input value={f.email} onChange={set("email")} placeholder="you@example.com" className="inp" data-testid="signup-email" /></Field>
              <Field label="City"><input value={f.city} onChange={set("city")} placeholder="Your city" className="inp" data-testid="signup-city" /></Field>
              <Field label="Preferred language">
                <select value={f.language} onChange={set("language")} className="inp" data-testid="signup-language">{LANGS.map((l) => <option key={l}>{l}</option>)}</select>
              </Field>
              <Field label="Referral code (optional)"><input value={f.referral_code} onChange={(e) => setF((p) => ({ ...p, referral_code: e.target.value.toUpperCase() }))} placeholder="e.g. BG1234" className="inp" data-testid="signup-referral" /></Field>
              <button onClick={next} className="w-full bg-coral hover:bg-coral-hover active:scale-[0.99] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all shadow-sm shadow-coral/30 tap" data-testid="signup-continue-btn">Continue <ArrowRight size={18} /></button>
              <p className="text-[11px] text-ink-600 text-center">We'll send a verification OTP to your mobile and email.</p>
            </div>
          )}
        </div>
      </div>
      <style>{`.inp{width:100%;margin-top:4px;background:var(--surface,#f6f6f4);border-radius:12px;padding:12px 14px;font-size:14px;outline:none}.inp:focus{box-shadow:0 0 0 2px #FF6B35}`}</style>
    </AuthShell>
  );
}

function Field({ label, required, children }) {
  return <div><label className="text-xs font-semibold text-ink-600">{label}{required && <span className="text-coral"> *</span>}</label>{children}</div>;
}
